import React, { FC } from 'react';
import Image from 'next/image';
import classNames from 'classnames';
import { TypeMediaCollectionVideo } from '@/core/generated-types';
import padlock from '@/public/assets/icons/padlock-locked.svg';
import play from '@/public/assets/icons/play.svg';
import pause from '@/public/assets/icons/pause.svg';
import { ProgressBar } from '@/components/common/ProgressBar';
import {
  pauseMuxPlayer,
  playMuxPlayer,
} from '@/components/mediaCollection/MediaCollectionVideo/MediaCollectionVideo';
import {
  captureExternalPlaybackEvent,
  PLAYBACK_EVENT_TYPES,
} from '@/utils/dataLayer';

export type SubChapterStatus = 'streaming' | 'locked' | 'inactive';

export interface MediaCollectionSubChapterProps {
  status: SubChapterStatus;
  subChapter: TypeMediaCollectionVideo;
  streamVideoOnDemand?: ((videoSysId: string) => void) | undefined;
  percentComplete?: number;
  isPreview?: boolean;
  isPreviousSubChapterStreaming?: boolean;
  isVideoPlaying?: boolean;
  setIsVideoPlaying?: (bool: boolean) => void;
}

function _getIconSrcForStatus(
  status: SubChapterStatus,
  isActivelyStreaming: boolean,
) {
  switch (status) {
    case 'streaming':
      return isActivelyStreaming ? pause : play;
    case 'locked':
      return padlock;
    case 'inactive':
      return play;
  }
}

function _getDisplayDuration(duration: number) {
  const minutes = Math.floor(duration / 60);
  const seconds = Math.floor(duration % 60);
  const secondsFormatted = seconds.toString().padStart(2, '0');

  return `${minutes}:${secondsFormatted}`;
}

const MediaCollectionSubChapter: FC<MediaCollectionSubChapterProps> = ({
  subChapter,
  streamVideoOnDemand,
  status,
  percentComplete,
  isPreview = false,
  isPreviousSubChapterStreaming = false,
  isVideoPlaying,
  setIsVideoPlaying,
}) => {
  const { title, video } = subChapter.fields;
  const isActivelyStreaming = !isPreview && !!isVideoPlaying;
  const iconImageSrc = _getIconSrcForStatus(status, isActivelyStreaming);
  const displayProgressBar = percentComplete !== undefined;

  function handleClick() {
    if (status === 'locked') {
      return;
    }

    // load this video in the mux player and start streaming
    if (status === 'inactive') {
      captureExternalPlaybackEvent(PLAYBACK_EVENT_TYPES.STREAM_ON_DEMAND, {
        title: subChapter.fields.title,
        percentComplete: percentComplete || 0,
      });

      if (streamVideoOnDemand) {
        streamVideoOnDemand(subChapter.fields.video.sys.id);
      }
    }

    // a status of streaming simply means the video is loaded in the mux player
    // isActivelyStreaming indicates if the video is actively playing or paused
    if (status === 'streaming') {
      if (isActivelyStreaming) {
        captureExternalPlaybackEvent(PLAYBACK_EVENT_TYPES.PAUSE, {
          title: subChapter.fields.title,
          percentComplete: percentComplete || 0,
        });
        if (setIsVideoPlaying) setIsVideoPlaying(false);
        pauseMuxPlayer();
      } else {
        captureExternalPlaybackEvent(PLAYBACK_EVENT_TYPES.PLAY, {
          title: subChapter.fields.title,
          percentComplete: percentComplete || 0,
        });
        if (setIsVideoPlaying) setIsVideoPlaying(true);
        playMuxPlayer();
      }
    }
  }

  const cursorPointerClass =
    status === 'locked' ? 'cursor-auto' : 'cursor-pointer';
  const isStreaming = status === 'streaming';

  return (
    <div
      className={classNames('-mx-4', {
        'bg-indigo-tint': isStreaming,
      })}
      data-testid="media-collection-sub-chapter"
    >
      <div
        className={classNames(
          'ml-11 mr-4 flex items-center justify-between gap-6 border-t border-y-charcoal-opacity-20 py-5 pr-4',
          {
            'border-none': isStreaming || isPreviousSubChapterStreaming,
          },
        )}
      >
        <div
          onClick={handleClick}
          className={`flex w-full select-none items-baseline gap-3 ${cursorPointerClass}`}
          data-testid="sub-chapter-click-handler"
        >
          <Image
            src={iconImageSrc}
            alt={status}
            height={15}
            width={15}
            className="h-[15px] w-[15px]"
          />
          <div className="flex w-full flex-col gap-2">
            <p
              className={classNames('text-left text-base', {
                'font-bold text-indigo': status === 'streaming',
                'text-indigo': status === 'inactive',
              })}
            >
              {title}
            </p>
            {displayProgressBar && (
              <ProgressBar
                percentage={percentComplete ?? 0}
                colorVariant={status === 'streaming' ? 'white-bg' : 'default'}
              />
            )}
          </div>
        </div>
        <p className="text-sm text-grey-dark">
          {_getDisplayDuration(video.fields.muxVideoUpload.duration)}
        </p>
      </div>
    </div>
  );
};

export default MediaCollectionSubChapter;
